import {useCallback, useEffect, useRef, useState} from 'react';
import {
  getWidgetSession,
  loadThreadMessages,
  logRealtime,
  logScroll,
  reportWidgetError,
  refreshWidgetSessionThread,
  updateWidgetSessionThreadId,
} from '@teo/widget-core';
import {useChat} from '../chat/ChatProvider.js';

export type HistoryStatus = 'idle' | 'loading' | 'ready' | 'error';

const THREAD_UPDATED_EVENT = 'teo-widget-thread-updated';

type UseLoadThreadHistoryOptions = {
  chatOpen: boolean;
  apiBase?: string;
  threadId?: number | string | null;
  enabled?: boolean;
  /** Called when thread id is resolved (enables WS subscription). */
  onThreadIdResolved?: (threadId: number | string) => void;
};

type UseLoadThreadHistoryResult = {
  historyStatus: HistoryStatus;
  retryHistory: () => void;
};

async function resolveThreadIdForFetch(
  apiBase: string,
  threadId?: number | string | null,
): Promise<number | string | null> {
  if (threadId != null && Number(threadId) > 0) {
    return threadId;
  }

  const sessionThreadId = getWidgetSession()?.threadId;
  if (sessionThreadId != null && Number(sessionThreadId) > 0) {
    return sessionThreadId;
  }

  return refreshWidgetSessionThread(apiBase);
}

function notifyThreadIdResolved(
  threadId: number | string,
  onThreadIdResolved?: (threadId: number | string) => void,
): void {
  const numericId = Number(threadId);
  if (!Number.isFinite(numericId) || numericId <= 0) return;

  updateWidgetSessionThreadId(numericId);
  onThreadIdResolved?.(threadId);
  logRealtime('thread', 'thread_id resolved for realtime', { threadId });
}

function isRealThreadId(value: number | string | null | undefined): boolean {
  return value != null && Number(value) > 0;
}

/**
 * Prefetch thread history on widget mount (before chat opens).
 * Refetches on open only when the list is still empty (slow network).
 */
export function useLoadThreadHistory({
  chatOpen,
  apiBase,
  threadId,
  enabled = true,
  onThreadIdResolved,
}: UseLoadThreadHistoryOptions): UseLoadThreadHistoryResult {
  const { syncFromServer, setThreadMeta, messages } = useChat();
  const [historyStatus, setHistoryStatus] = useState<HistoryStatus>('idle');
  const wasOpenRef = useRef(chatOpen);
  const requestIdRef = useRef(0);
  const loadedForThreadRef = useRef<number | string | null>(null);
  const messageCountRef = useRef(messages.length);
  const onThreadIdResolvedRef = useRef(onThreadIdResolved);
  const prevThreadIdRef = useRef(threadId);
  onThreadIdResolvedRef.current = onThreadIdResolved;
  messageCountRef.current = messages.length;

  const fetchMessages = useCallback(
    async (reason: string) => {
      if (!enabled || !apiBase) return;


      const backgroundFetch = messageCountRef.current > 0;

      if (backgroundFetch && reason === 'prefetch') {
        logScroll('history', 'skip prefetch — local messages in chat', {
          reason,
          messageCount: messageCountRef.current,
        });
        return;
      }

      const requestId = ++requestIdRef.current;

      if (!backgroundFetch) {
        setHistoryStatus('loading');
      }

      logScroll('history', reason, {
        requestId,
        threadId,
        apiBase,
        backgroundFetch,
        messageCount: messageCountRef.current,
      });

      try {
        const resolvedThreadId = await resolveThreadIdForFetch(apiBase, threadId);
        if (requestId !== requestIdRef.current) return;
        if (!resolvedThreadId || Number(resolvedThreadId) <= 0) {
          logScroll('history', 'no thread id — skip fetch', { requestId, reason });
          if (!backgroundFetch) {
            setHistoryStatus('ready');
          }
          return;
        }

        notifyThreadIdResolved(
          resolvedThreadId,
          onThreadIdResolvedRef.current,
        );

        const threadChanged = loadedForThreadRef.current !== resolvedThreadId;
        if (
          !threadChanged &&
          loadedForThreadRef.current === resolvedThreadId &&
          messageCountRef.current > 0 &&
          reason === 'prefetch'
        ) {
          logScroll('history', 'skip — already loaded', {
            requestId,
            reason,
            resolvedThreadId,
            messageCount: messageCountRef.current,
          });
          if (!backgroundFetch) {
            setHistoryStatus('ready');
          }
          return;
        }

        logScroll('history', 'loading messages', {
          requestId,
          reason,
          resolvedThreadId,
          backgroundFetch,
        });
        const result = await loadThreadMessages(apiBase, resolvedThreadId);
        if (requestId !== requestIdRef.current) return;

        logScroll('history', 'syncFromServer', {
          requestId,
          reason,
          count: result.messages.length,
          backgroundFetch,
        });
        setThreadMeta({ managerName: result.managerName });
        syncFromServer(result.messages);
        loadedForThreadRef.current = resolvedThreadId;
        if (!backgroundFetch) {
          setHistoryStatus('ready');
        }
      } catch (error) {
        if (requestId !== requestIdRef.current) return;
        console.warn('[Teo Widget] Failed to load thread history', error);
        reportWidgetError(error, {
          source: 'history',
          fatal: false,
        });
        if (!backgroundFetch) {
          setHistoryStatus('error');
        }
      }
    },
    [apiBase, enabled, setThreadMeta, syncFromServer, threadId],
  );

  const retryHistory = useCallback(() => {
    loadedForThreadRef.current = null;
    void fetchMessages('retry');
  }, [fetchMessages]);

  useEffect(() => {
    void fetchMessages('prefetch');
  }, [fetchMessages]);

  useEffect(() => {
    const justOpened = chatOpen && !wasOpenRef.current;
    wasOpenRef.current = chatOpen;

    if (!enabled || !justOpened || !apiBase) return;
    if (messageCountRef.current > 0) {
      logScroll('history', 'open — skip fetch, messages in memory', {
        messageCount: messageCountRef.current,
      });
      return;
    }

    void fetchMessages('open');
  }, [apiBase, chatOpen, enabled, fetchMessages]);

  useEffect(() => {
    const prev = prevThreadIdRef.current;
    if (prev === threadId) return;

    const hadNoThread = !isRealThreadId(prev ?? null);
    const hasThreadNow = isRealThreadId(threadId ?? null);
    prevThreadIdRef.current = threadId;

    if (!enabled || !apiBase) return;

    if (hadNoThread && hasThreadNow) {
      loadedForThreadRef.current = null;
      void fetchMessages('thread-assigned');
      return;
    }

    if (hasThreadNow && isRealThreadId(prev ?? null) && prev !== threadId) {
      loadedForThreadRef.current = null;
      void fetchMessages('thread-changed');
    }
  }, [apiBase, enabled, fetchMessages, threadId]);

  useEffect(() => {
    if (!enabled || !apiBase) return;

    const onThreadUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ threadIdChanged?: boolean }>).detail;
      if (!detail?.threadIdChanged) return;
      loadedForThreadRef.current = null;
      void fetchMessages('thread-updated');
    };

    window.addEventListener(THREAD_UPDATED_EVENT, onThreadUpdated);
    return () => window.removeEventListener(THREAD_UPDATED_EVENT, onThreadUpdated);
  }, [apiBase, enabled, fetchMessages]);

  return { historyStatus, retryHistory };
}
